import { getAnalytics, logEvent } from "firebase/analytics";
import { Eye, Menu } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ConsultantHamburger from './ConsultantHamburger';
import NotificationBell from './NotificationBell';

const ConsultantJobDescription = () => {
  const [job, setJob] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const { jobId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const analytics = getAnalytics();
    logEvent(analytics, 'page_view', {
      page_title: 'Job Description',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });

    checkAuthStatus();
    fetchJobDetails();
    fetchUser();
  }, [jobId]);

  const checkAuthStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAuthenticated(!!session);
  };

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUser(data);
    } catch (error) {
      console.error('Error fetching user:', error.message);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      let query = supabase
        .from('jobs')
        .select(`
          *,
          clients (name),
          jobs_epic_certifications (
            epic_applications (name)
          )
        `)
        .eq('job_id', jobId)
        .eq('status', 'Active')
        .single();
  
      if (user) {
        query = query.select(`
          *,
          clients (name),
          jobs_epic_certifications (
            epic_applications (name)
          ),
          user_jobs!left (status)
        `).eq('user_jobs.user_id', user.id);
      }
  
      const { data, error } = await query;
  
      if (error) throw error;
  
      if (data) {
        if (data.status !== 'Active') {
          setError('This job is no longer active.');
        } else {
          setJob(data);
          if (user) {
            setApplicationStatus(data.user_jobs[0]?.status || null);
          }
        }
      } else {
        setError('Job not found');
      }
  
      // Log view in Firebase Analytics
      const analytics = getAnalytics();
      logEvent(analytics, 'job_view', {
        job_id: jobId,
        job_title: data.title,
        is_authenticated: !!user
      });
  
      // Log view in Supabase for rate limiting
      await supabase.from('job_views').insert({
        job_id: jobId,
        user_id: user ? user.id : null,
        viewed_at: new Date()
      });
  
    } catch (error) {
      console.error('Error fetching job details:', error.message);
      setError('Failed to load job details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyOrUpdate = () => {
    const analytics = getAnalytics();
    let eventName;
    if (!applicationStatus) {
      eventName = 'start_application';
    } else if (applicationStatus === 'Application Withdrawn') {
      eventName = 'reapply';
    } else {
      eventName = 'update_application';
    }
    logEvent(analytics, eventName, {
      job_id: jobId,
      job_title: job.title,
    });
    navigate(`/app/apply-job/${jobId}`);
  };

  const showAuthRedirectPopup = () => {
    alert("You need to create an account to perform this action. Redirecting you to sign up.");
  };

  const handleAuthAction = async (action) => {
    if (!isAuthenticated) {
      showAuthRedirectPopup();
      navigate('/app/auth', { state: { from: `/app/job/${jobId}`, action } });
    } else {
      if (action === 'apply') {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("No authenticated user found");
  
          // Check if an application already exists
          const { data: existingApplication, error: fetchError } = await supabase
            .from('user_jobs')
            .select('*')
            .eq('user_id', user.id)
            .eq('job_id', jobId)
            .single();
  
          if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
  
          if (existingApplication) {
            // Get the latest version number
            const { data: latestAudit, error: versionError } = await supabase
              .from('applications_audit')
              .select('version')
              .eq('user_job_id', existingApplication.user_job_id)
              .order('version', { ascending: false })
              .limit(1)
              .single();
  
            if (versionError && versionError.code !== 'PGRST116') throw versionError;
  
            const newVersion = latestAudit ? latestAudit.version + 1 : 1;
  
            // Save existing application to applications_audit
            const { error: auditError } = await supabase
              .from('applications_audit')
              .insert({
                ...existingApplication,
                user_job_id: existingApplication.user_job_id,
                version: newVersion
              });
  
            if (auditError) throw auditError;
  
            // Update existing application
            const { error: updateError } = await supabase
              .from('user_jobs')
              .update({
                status: 'Application in Progress',
                terms_and_conditions: false,
                last_updated_at: new Date().toISOString()
              })
              .eq('user_id', user.id)
              .eq('job_id', jobId);
  
            if (updateError) throw updateError;
          } else {
            // Create new application
            const { error: insertError } = await supabase
              .from('user_jobs')
              .insert({
                user_id: user.id,
                job_id: jobId,
                status: 'Application in Progress',
                terms_and_conditions: false,
                created_at: new Date().toISOString()
              });
  
            if (insertError) throw insertError;
          }
  
          navigate(`/app/apply-job/${jobId}`);
        } catch (error) {
          console.error('Error handling application:', error.message);
          alert('An error occurred while processing your application. Please try again.');
        }
      } else if (action === 'refer') {
        navigate('/app/solicit-refer', { state: { jobId: job.job_id } });
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getTimeSincePosted = (postedDate) => {
    const now = new Date();
    const posted = new Date(postedDate);
    const diffInMilliseconds = now - posted;
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  
    if (diffInDays === 0) {
      return "Posted today";
    } else if (diffInDays === 1) {
      return "Posted yesterday";
    } else {
      return `Posted ${diffInDays} days ago`;
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!job) return <div>No job found</div>;

  const showApplyButton = !applicationStatus || 
  (applicationStatus !== 'Interview Rejected' && applicationStatus !== 'Contract Not Offered');

  return (
    <>
      <Helmet>
        <title>{`${job.title} | fetchConsultant`}</title>
        <meta name="description" content={`View details and apply for the ${job.title} position at ${isAuthenticated ? job.clients.name : 'a leading healthcare organization'}.`} />
      </Helmet>
      <div className="min-h-screen bg-gray-100 flex">
        <ConsultantHamburger user={user} currentPage="job-description" isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
        <div className="flex-1 flex flex-col md:ml-64">
          <header className="bg-white shadow-md p-4 flex justify-between items-center">
            <div className="flex items-center">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="mr-4 md:hidden">
                <Menu size={24} />
              </button>
              <h1 className="text-xl font-bold">{job.title}</h1>
            </div>
            {isAuthenticated && (
            <div className="flex items-center">
              <NotificationBell />
            </div>
            )}
          </header>
          <main className="flex-grow container mx-auto px-4 py-8">
            <div className="fetch-card bg-white rounded-lg shadow-md p-6">
            {isAuthenticated ? (
              <h2 className="text-2xl font-bold text-left">{job.clients.name}</h2>
            ) : (
              <button 
                onClick={() => handleAuthAction('viewClient')}
                className="text-brandPrimary hover:text-indigo-800 flex items-center"
              >
                <Eye size={20} className="mr-2" />
                Show Client's Name
              </button>
            )}
            <div className="mt-1">
              <h3 className="visually-hidden">Job Description</h3> {/* Hidden but SEO-friendly */}
              <div 
                className="job-description" 
                dangerouslySetInnerHTML={{ __html: job.description }}
              ></div>
            </div>
            <div className="space-y-6 text-left">
              <div>
                <p className="font-bold text-gray-700">Ideal Start Date</p>
                <p>{formatDate(job.ideal_start_date)}</p>
              </div>
              <div>
                <p className="font-bold text-gray-700">Onsite or Remote</p>
                <p>{job.travel === '100% Remote' ? 'Remote' : job.travel}</p>
              </div>
              {job.travel !== '100% Remote' && (
                <div>
                  <p className="font-bold text-gray-700">City/State</p>
                  <p>{`${job.city}, ${job.state}`}</p>
                </div>
              )}
              <div>
                <p className="font-bold text-gray-700">Duration</p>
                <p>{job.duration}</p>
              </div>
              <div>
                <p className="font-bold text-gray-700">Extension Probability</p>
                <p>{job.extension_possibility}</p>
              </div>
              {job.jobs_epic_certifications.length > 0 && (
                <div>
                  <p className="font-bold text-gray-700">Required Certifications</p>
                  <ul className="list-disc list-inside ml-4">
                    {job.jobs_epic_certifications.map((cert, index) => (
                      <li key={index}>{cert.epic_applications.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {job.experience && (
              <div>
                <p className="font-bold text-gray-700">Minimum Experience</p>
                <div 
                  className="job-description" 
                  dangerouslySetInnerHTML={{ __html: job.experience }}
                ></div>
              </div>
              )}
              {job.education && (
                <div>
                  <p className="font-bold text-gray-700">Education Requirement</p>
                  <p>{job.education}</p>
                </div>
              )}
              {job.referral_bonus && (
                <div>
                  <p className="font-bold text-gray-700">Referral Bonus</p>
                  <p>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(job.referral_bonus)}</p>
                </div>
              )}
              {job.posted_at && (
                <div>
                  <p className="font-bold text-gray-700">Posted Date</p>
                  <p>{formatDate(job.posted_at)}</p>
                </div>
              )}
              <div>
                <p className="font-bold text-gray-700">Status</p>
                <p>{job.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 text-left mb-4">{getTimeSincePosted(job.posted_at)}</p>
              </div>
            </div>
  
            <div className="mt-8 flex justify-between">
              {showApplyButton && (
                <button 
                  onClick={handleApplyOrUpdate}
                  className="fetch-button w-5/12 bg-brandPrimary text-white"
                >
                  {applicationStatus === 'Application Withdrawn' ? 'Reapply' :
                  applicationStatus ? 'Update App' : 'Apply'}
                </button>
              )}
              <button 
                onClick={() => handleAuthAction('refer')}
                className="fetch-button w-5/12 bg-indigo-100 text-brandPrimary hover:bg-indigo-200"
              >
                Refer
              </button>
            </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default ConsultantJobDescription;